const SCROLL_SPEED_MOUSE_DESKTOP = 4
const SCROLL_SPEED_MOUSE_PHONE = 2

const horizontal_scroll_controller = {
	isActive: false,
	requestedActive: false,
	suppressedByLenis: false,
	lenisObserver: null,
	container: null,
	overlayContext: false,
	currentScrollLeft: 0,
	currentScrollProgress: 0,
	wheelHandler: null,
	newPageHandler: null,
	viewportResizeHandler: null,
	wheelListenerOptions: { passive: false, capture: true },
	disableForPhoneSetting: true,
	bodyHasCustomPhoneLayout: null,
	overlayHasCustomPhoneLayout: null,
	phoneBreakpoint: null,
	showHorizontalScrollbar: false,
	/** When switching to project overlay, store main page horizontal-scroll state so we can restore on overlay close */
	bodyRequestedActive: null,
	bodyScrollState: null,

	i() {
		this.newPageHandler = this.handleNewPageShown.bind(this)
		this.disableForPhoneSetting = typeof layData !== 'undefined' && typeof layData.horizontal_scroll_disable_for_phone !== 'undefined' ? !!layData.horizontal_scroll_disable_for_phone : true
		this.showHorizontalScrollbar = typeof layData !== 'undefined' && typeof layData.horizontal_scroll_show_scrollbar !== 'undefined' ? !!layData.horizontal_scroll_show_scrollbar : false
		const parsedBreakpoint = typeof layData !== 'undefined' && layData.breakpoint ? parseInt(layData.breakpoint, 10) : null
		this.phoneBreakpoint = typeof parsedBreakpoint === 'number' && !isNaN(parsedBreakpoint) ? parsedBreakpoint : null
		this.bodyHasCustomPhoneLayout = this.detectCustomPhoneLayout(false)
		this.overlayHasCustomPhoneLayout = this.detectCustomPhoneLayout(true)
		this.viewportResizeHandler = this.onViewportResize.bind(this)
		window.addEventListener('resize', this.viewportResizeHandler)

		if (window.laytheme && window.laytheme.on) {
			window.laytheme.on('newpageshown', this.newPageHandler)
			window.laytheme.on('before_content_replacement_for_project_overlay', this.handleBeforeProjectOverlayOpen.bind(this))
			window.laytheme.on('projectoverlayclosed', this.handleProjectOverlayClosed.bind(this))
		}

		const html = document.documentElement
		const initialActive = (html && html.classList.contains('horizontal-scroll-active')) || (html && html.dataset.horizontalScrollActive === '1')
		this.applyState(initialActive, false)
		this.observeLenisState()
	},

	handleBeforeProjectOverlayOpen() {
		if (this.overlayContext) {
			return
		}
		this.bodyRequestedActive = this.requestedActive
		this.captureBodyScrollState()
		this.toggleUnderlayLayout(this.bodyRequestedActive && !this.shouldDisableForPhone(false))
	},

	handleNewPageShown(layoutObj, type, obj, meta) {
		const isOverlay = meta && meta.isProjectOverlay
		const hasExplicitActiveState = !!(obj && typeof obj.horizontal_scroll_active !== 'undefined')
		const isActive = hasExplicitActiveState ? !!obj.horizontal_scroll_active : false
		const hasCustomPhoneLayout = obj && typeof obj.has_custom_phone_layout !== 'undefined' ? !!obj.has_custom_phone_layout : null
		// When opening a project overlay, keep the main-page requested
		// horizontal-scroll state. The overlay has its own content scope, and
		// closing it should restore the page underneath instead of inheriting
		// whatever the project overlay wanted.
		if (isOverlay && !this.overlayContext) {
			if (this.bodyRequestedActive === null) {
				this.bodyRequestedActive = this.requestedActive
			}
			if (this.bodyScrollState === null) {
				this.captureBodyScrollState()
			}
			this.toggleUnderlayLayout(this.bodyRequestedActive && !this.shouldDisableForPhone(false))
		}
		// While the overlay is open, `newpageshown(isOverlay: false)` may
		// still fire for the underlay (direct-load underlay fetch, overlay
		// close, or similar background refresh). Applying that state
		// immediately would switch the live scroll container from
		// `#lay-project-overlay` back to `body` while the overlay is still
		// visible. During close, store the underlay state and apply it only
		// after `projectoverlayclosed`.
		if (!isOverlay && this.overlayContext) {
			if (this.isClosingProjectOverlay()) {
				// The history state used when returning to the existing underlay does
				// not necessarily contain page layout data. Keep the state captured
				// before opening the overlay unless an explicit value was provided.
				if (hasExplicitActiveState) {
					this.bodyRequestedActive = isActive
				}
				this.storeCustomPhoneLayoutFlag(false, hasCustomPhoneLayout)
				// The underlay DOM was replaced immediately before `newpageshown`.
				// Put the new horizontal container at its saved position in the same
				// frame, while the project overlay still covers it. Waiting until
				// `projectoverlayclosed` makes both the content and the native
				// scrollbar visibly jump at the end of the close animation.
				const bodyActive = this.bodyRequestedActive && !this.shouldDisableForPhone(false)
				if (bodyActive) {
					this.restoreBodyScrollState(false)
				}
			}
			return
		}
		// Wait for DOM replacement to finish
		setTimeout(() => {
			// When switching to overlay, save main page horizontal scroll position now (container is still body's) so we can restore it when overlay closes
			if (isOverlay && this.container) {
				this.saveScrollState()
			}
			this.storeCustomPhoneLayoutFlag(isOverlay, hasCustomPhoneLayout)
			this.applyState(isActive, isOverlay)
		}, 0)
	},

	handleProjectOverlayClosed() {
		// Restore the main-page horizontal-scroll state captured before /
		// during the overlay close. This mirrors the ScrollSlider fix: wait
		// until the overlay is gone before re-binding page-level scrolling.
		const fromStored = this.bodyRequestedActive !== null
		const bodyActive = fromStored ? this.bodyRequestedActive : document.documentElement && document.documentElement.dataset.horizontalScrollActive === '1'
		this.applyState(!!bodyActive, false)
		if (bodyActive) {
			this.restoreBodyScrollState()
		}
		this.toggleUnderlayLayout(false)
		this.bodyRequestedActive = null
		this.bodyScrollState = null
	},

	applyState(isActive, isOverlay) {
		this.requestedActive = !!isActive
		this.overlayContext = !!isOverlay

		const container = this.getContainer(this.overlayContext)
		this.container = container
		const disableForPhone = this.shouldDisableForPhone(this.overlayContext)
		const finalActive = this.requestedActive && !disableForPhone

		this.suppressedByLenis = false
		this.toggleRoot(finalActive)
		this.toggleOverlayMode(finalActive && this.overlayContext)

		// On main page: disable Lenis when horizontal scroll is active so it doesn't handle wheel; re-enable when inactive
		if (!this.overlayContext && window.smooth_scroll_controller && typeof window.smooth_scroll_controller.setBodyLenisStopped === 'function') {
			window.smooth_scroll_controller.setBodyLenisStopped(finalActive)
		}

		if (finalActive) {
			this.enableHandlers()
		} else {
			this.disableHandlers()
		}
	},

	toggleRoot(isActive) {
		const html = document.documentElement
		if (html) {
			html.classList.toggle('horizontal-scroll-active', isActive)
			html.classList.toggle('horizontal-scroll-show-scrollbar', !!this.showHorizontalScrollbar)
			html.dataset.horizontalScrollActive = isActive ? '1' : '0'
		}
		if (document.body) {
			document.body.dataset.horizontalScrollActive = isActive ? '1' : '0'
			document.body.classList.toggle('horizontal-scroll-show-scrollbar', !!this.showHorizontalScrollbar)
		}
	},

	toggleUnderlayLayout(isActive) {
		const html = document.documentElement
		if (html) {
			html.classList.toggle('horizontal-scroll-underlay-active', !!isActive)
		}
		if (document.body) {
			document.body.classList.toggle('horizontal-scroll-underlay-active', !!isActive)
		}
	},

	enableHandlers() {
		this.isActive = true
		this.updateContainer(true)

		if (!this.wheelHandler) {
			this.wheelHandler = this.onWheel.bind(this)
			window.addEventListener('wheel', this.wheelHandler, this.wheelListenerOptions)
		}
	},

	disableHandlers() {
		this.saveScrollState()
		this.isActive = false

		if (this.wheelHandler) {
			window.removeEventListener('wheel', this.wheelHandler, this.wheelListenerOptions)
			this.wheelHandler = null
		}
	},

	onViewportResize() {
		if (!this.requestedActive && !this.isActive) {
			return
		}
		const shouldDisable = this.shouldDisableForPhone(this.overlayContext)
		if ((shouldDisable && this.isActive) || (!shouldDisable && this.requestedActive && !this.isActive)) {
			this.applyState(this.requestedActive, this.overlayContext)
			return
		}
		if (this.isActive) {
			this.updateContainer(true)
		}
	},

	updateContainer(preservePosition = true) {
		if (preservePosition) {
			this.saveScrollState()
		}

		const newContainer = this.getContainer(this.overlayContext)
		this.container = newContainer
		if (this.container) {
			if (preservePosition) {
				this.restoreScrollState(this.container)
			} else {
				this.currentScrollLeft = this.container.scrollLeft
				const maxScroll = Math.max(this.container.scrollWidth - this.container.clientWidth, 0)
				this.currentScrollProgress = maxScroll > 0 ? this.container.scrollLeft / maxScroll : 0
			}
		}
		return this.container
	},

	onWheel(event) {
		if (!this.isActive || event.ctrlKey) {
			return
		}

		// If a page-overlay is open, don't intercept wheel events inside it.
		// Otherwise overlay scrolling can feel "broken" because we preventDefault().
		// Project overlays are handled via overlayContext, so we only block page overlays here.
		try {
			const target = event.target
			if (target && typeof target.closest === 'function') {
				const $pageOverlay = target.closest('.lay-overlay.lay-show-overlay')
				const isInsideProjectOverlay = target.closest('#lay-project-overlay')
				if ($pageOverlay && !isInsideProjectOverlay) {
					return
				}
			}
		} catch (e) {
			// ignore - don't break scrolling if closest() isn't available for some reason
		}

		const container = this.container || this.updateContainer(true)
		if (!container) {
			return
		}

		// Only intercept when vertical scrolling dominates
		if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) {
			return
		}

		const speed = this.isPhoneViewport() ? SCROLL_SPEED_MOUSE_PHONE : SCROLL_SPEED_MOUSE_DESKTOP
		container.scrollLeft += event.deltaY * speed
		this.saveScrollState()
		event.preventDefault()
		event.stopPropagation()
	},

	isLenisActive() {
		// When we've stopped Lenis for horizontal scroll, it must not count as active (so we keep horizontal-scroll-active)
		if (typeof window !== 'undefined' && window.lenisInstance && typeof window.lenisInstance.isStopped === 'boolean' && window.lenisInstance.isStopped) {
			return false
		}
		if (typeof window !== 'undefined' && window.smooth_scroll_controller && typeof window.smooth_scroll_controller.isBodySmoothScrollEnabled === 'function') {
			if (!window.smooth_scroll_controller.isBodySmoothScrollEnabled()) {
				return false
			}
		}
		const html = document.documentElement
		if (html && html.classList.contains('lenis')) {
			return true
		}
		if (typeof window !== 'undefined' && window.lenisInstance) {
			return true
		}
		// Lenis is active when smooth scroll is on and (not "disable on Mac" or not on Mac)
		if (typeof layData === 'undefined' || layData.misc_options_smooth_scroll !== 'on') {
			return false
		}
		if (layData.misc_options_smooth_scroll_disable_mac === 'on') {
			var ua = navigator.userAgent || ''
			var platform = navigator.platform || ''
			var isAppleDevice = /Macintosh|Mac OS X|iPhone|iPad|iPod/i.test(ua) || platform.toLowerCase().indexOf('mac') >= 0
			if (isAppleDevice) return false
		}
		return true
	},

	observeLenisState() {
		if (this.lenisObserver || typeof MutationObserver === 'undefined') {
			return
		}
		const html = document.documentElement
		if (!html) {
			return
		}
		this.lenisObserver = new MutationObserver(() => {
			if (this.isLenisActive()) {
				// If horizontal scroll is currently requested/active, don't let Lenis
				// flip it off (e.g. right after overlay close when smooth scroll resumes).
				if (this.requestedActive) {
					if (window.smooth_scroll_controller && typeof window.smooth_scroll_controller.setBodyLenisStopped === 'function') {
						window.smooth_scroll_controller.setBodyLenisStopped(true)
					}
					this.suppressedByLenis = false
					return
				}

				this.toggleRoot(false)
				this.toggleOverlayMode(false)
				this.disableHandlers()
				this.suppressedByLenis = true
			} else if (this.suppressedByLenis && this.requestedActive) {
				this.applyState(this.requestedActive, this.overlayContext)
			}
		})
		this.lenisObserver.observe(html, { attributes: true, attributeFilter: ['class'] })
	},

	saveScrollState() {
		if (!this.container) {
			return
		}
		this.currentScrollLeft = this.container.scrollLeft
		const maxScroll = Math.max(this.container.scrollWidth - this.container.clientWidth, 0)
		this.currentScrollProgress = maxScroll > 0 ? this.container.scrollLeft / maxScroll : 0
	},

	restoreScrollState(container) {
		if (!container) {
			return
		}
		const maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0)
		let scrollLeft = this.currentScrollProgress * maxScroll
		if (!isFinite(scrollLeft)) {
			scrollLeft = this.currentScrollLeft || 0
		}
		scrollLeft = Math.min(Math.max(scrollLeft, 0), maxScroll)
		container.scrollLeft = scrollLeft
		this.currentScrollLeft = scrollLeft
		this.currentScrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0
	},
	captureBodyScrollState() {
		const container = this.getContainer(false)
		if (!container) {
			this.bodyScrollState = null
			return
		}
		const maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0)
		this.bodyScrollState = {
			scrollLeft: container.scrollLeft,
			maxScroll: maxScroll,
			progress: maxScroll > 0 ? container.scrollLeft / maxScroll : 0,
		}
	},
	restoreBodyScrollState(updateControllerState = true) {
		if (!this.bodyScrollState) {
			return
		}
		const container = this.getContainer(false)
		if (!container) {
			return
		}
		const maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0)
		const sameScrollRange = Math.abs(maxScroll - this.bodyScrollState.maxScroll) < 1
		let scrollLeft = sameScrollRange ? this.bodyScrollState.scrollLeft : this.bodyScrollState.progress * maxScroll
		if (!isFinite(scrollLeft)) {
			scrollLeft = 0
		}
		scrollLeft = Math.min(Math.max(scrollLeft, 0), maxScroll)
		container.scrollLeft = scrollLeft
		if (updateControllerState) {
			this.container = container
			this.currentScrollLeft = scrollLeft
			this.currentScrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0
		}
	},
	getContainer(isOverlayContext) {
		const rootSelector = isOverlayContext ? '#lay-project-overlay .lay-content' : 'body>.lay-content'
		const selectors = []
		if (this.shouldUseCustomPhoneGrid(isOverlayContext)) {
			selectors.push(`${rootSelector} #custom-phone-grid .grid-inner`, `${rootSelector} #custom-phone-grid`)
			selectors.push(`${rootSelector} #grid .grid-inner`, `${rootSelector} #grid`)
		} else {
			selectors.push(`${rootSelector} #grid .grid-inner`, `${rootSelector} #grid`)
			selectors.push(`${rootSelector} #custom-phone-grid .grid-inner`, `${rootSelector} #custom-phone-grid`)
		}

		for (let i = 0; i < selectors.length; i++) {
			const selector = selectors[i]
			if (!selector) {
				continue
			}
			const node = document.querySelector(selector)
			if (node) {
				return node
			}
		}

		if (isOverlayContext) {
			return document.querySelector('#lay-project-overlay .lay-content #grid .grid-inner') || document.querySelector('#lay-project-overlay .lay-content #grid') || document.querySelector('#lay-project-overlay .lay-content #custom-phone-grid .grid-inner') || document.querySelector('#lay-project-overlay .lay-content #custom-phone-grid')
		}

		return document.querySelector('body>.lay-content #grid .grid-inner') || document.querySelector('body>.lay-content #grid') || document.querySelector('body>.lay-content #custom-phone-grid .grid-inner') || document.querySelector('body>.lay-content #custom-phone-grid')
	},
	shouldDisableForPhone(isOverlayContext) {
		if (!this.isPhoneViewport()) {
			return false
		}
		if (this.disableForPhoneSetting) {
			return true
		}
		return !this.getHasCustomPhoneLayout(isOverlayContext)
	},
	shouldUseCustomPhoneGrid(isOverlayContext) {
		return this.isPhoneViewport() && this.getHasCustomPhoneLayout(isOverlayContext)
	},
	isPhoneViewport() {
		if (typeof window === 'undefined') {
			return false
		}
		return !!(window.utility && window.utility.isPhoneSize)
	},
	isClosingProjectOverlay() {
		// `newpageshown(isOverlay:false)` also happens in harmless situations.
		// Only treat it as a deferred underlay restore when the project overlay
		// controller is actually in its closing state.
		return (
			typeof window !== 'undefined' &&
			window.projects_overlays &&
			typeof window.projects_overlays.shouldCloseOverlay === 'function' &&
			window.projects_overlays.shouldCloseOverlay() === true
		)
	},
	getHasCustomPhoneLayout(isOverlayContext) {
		if (isOverlayContext) {
			if (typeof this.overlayHasCustomPhoneLayout === 'boolean') {
				return this.overlayHasCustomPhoneLayout
			}
			this.overlayHasCustomPhoneLayout = this.detectCustomPhoneLayout(true)
			return this.overlayHasCustomPhoneLayout
		}
		if (typeof this.bodyHasCustomPhoneLayout === 'boolean') {
			return this.bodyHasCustomPhoneLayout
		}
		this.bodyHasCustomPhoneLayout = this.detectCustomPhoneLayout(false)
		return this.bodyHasCustomPhoneLayout
	},
	detectCustomPhoneLayout(isOverlayContext) {
		const root = isOverlayContext ? document.querySelector('#lay-project-overlay>.lay-content') : document.querySelector('body>.lay-content')
		if (!root || !root.classList) {
			return false
		}
		if (root.classList.contains('hascustomphonegrid')) {
			return true
		}
		return !!root.querySelector('#custom-phone-grid')
	},
	storeCustomPhoneLayoutFlag(isOverlayContext, value) {
		if (typeof value === 'boolean') {
			if (isOverlayContext) {
				this.overlayHasCustomPhoneLayout = value
			} else {
				this.bodyHasCustomPhoneLayout = value
			}
			return
		}
		if (isOverlayContext) {
			this.overlayHasCustomPhoneLayout = this.detectCustomPhoneLayout(true)
		} else {
			this.bodyHasCustomPhoneLayout = this.detectCustomPhoneLayout(false)
		}
	},
	toggleOverlayMode(isActive) {
		const overlay = document.getElementById('lay-project-overlay')
		if (!overlay) {
			return
		}
		overlay.classList.toggle('horizontal-scroll-mode', !!isActive)
	},
}

export default horizontal_scroll_controller
